import React, { useRef } from 'react';
import { cx, css } from '@linaria/atomic';
import { AnimatePresence, motion, type MotionProps } from 'motion/react';
import { Icon } from '@frontend/icons';
import type { CommonHTMLImageAttributes } from '@frontend/types';
import { theme } from '@frontend/theme';
import { splitStylesProp, type StylesProp } from '../../helpers';
import { Button } from '../button';
import { SkeletonLoader } from '../loader/skeleton-loader';
import { Text } from '../text';
import { useImageLoader, MAX_RETRY_LIMIT } from './hooks';

export type ImageProps = MotionProps &
  CommonHTMLImageAttributes & {
    placeholderSrc?: string;
    aspectRatio?: number;
    borderRadius?: number | string;
    className?: string;
    containerStyle?: StylesProp;
    containerClassName?: string;
    onRetry?: () => void;
    objectFit?: 'fill' | 'cover' | 'contain';
    allowRetry?: boolean;
    imageStyle?: StylesProp;
  };

const Placeholder: React.FC<{
  placeholderSrc?: string;
  alt?: string;
  imageStyles: string;
  customImageCss?: any;
}> = ({ placeholderSrc, alt, imageStyles, customImageCss }) => (
  <motion.div
    className={placeholderStyle}
    initial={{ opacity: 1 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0, transition: { duration: 0.3 } }}
  >
    {placeholderSrc ? (
      <img
        src={placeholderSrc}
        alt={`Placeholder for ${alt}`}
        {...(customImageCss !== undefined ? { css: customImageCss } : undefined)}
        className={cx(imageStyles, placeholderImgExtraStyle)}
      />
    ) : (
      <SkeletonLoader width='100%' height='100%' />
    )}
  </motion.div>
);

const ErrorState: React.FC<{
  alt?: string;
  allowRetry: boolean;
  retryCount: number;
  handleRetry: () => void;
}> = ({ alt, allowRetry, retryCount, handleRetry }) => (
  <div className={errorContainerStyle} role='alert' aria-live='polite'>
    <Icon name='error-badge' color='error' className={errorIconStyle} aria-hidden='true' size={60} />
    <div className={errorTextContainerStyle}>
      <Text textAlign='center' color='subdued' weight='bold' className={errorTextMarginZero}>
        Failed to load image
      </Text>
      {!!alt && (
        <Text className={errorTextSubStyle} textAlign='center' size='small' weight='semibold' color='light'>
          {alt}
        </Text>
      )}
    </div>
    {allowRetry && retryCount < MAX_RETRY_LIMIT ? (
      <Button onClick={handleRetry} aria-label='Retry loading image'>
        Retry
      </Button>
    ) : null}
  </div>
);

export const ImageComponent: React.FC<ImageProps> = (props) => {
  const {
    src,
    alt,
    placeholderSrc,
    width,
    height,
    aspectRatio,
    borderRadius = 0,
    loading = 'lazy',
    className = '',
    containerStyle,
    containerClassName,
    onRetry,
    imageStyle: imgStyle,
    objectFit = 'cover',
    allowRetry = true,
    ...rest
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);

  const { state, isImageAlreadyLoaded, imageControls, handleImgRef, onLoad, onError, handleRetry } = useImageLoader(
    src || '',
    onRetry
  );

  const { css: customContainerCss, className: customContainerClassName } = splitStylesProp(containerStyle);
  const { css: customImageCss, className: customImageClassName } = splitStylesProp(imgStyle);

  const containerClassNameCompiled = cx(
    imageContainerStyle,
    customContainerClassName,
    className,
    containerClassName || ''
  );

  const imageStylesClass = cx(imageStyle, objectFitStyles[objectFit], customImageClassName);

  return (
    <div
      ref={containerRef}
      {...(customContainerCss !== undefined ? { css: customContainerCss } : undefined)}
      className={containerClassNameCompiled}
      style={{ width, height, aspectRatio, borderRadius }}
      aria-busy={state.status === 'loading'}
    >
      <AnimatePresence>
        {state.status === 'loading' && (
          <Placeholder
            placeholderSrc={placeholderSrc}
            alt={alt}
            imageStyles={imageStylesClass}
            customImageCss={customImageCss}
          />
        )}
      </AnimatePresence>

      {state.status !== 'error' && (
        <motion.img
          src={src}
          alt={alt}
          {...(customImageCss !== undefined ? { css: customImageCss } : undefined)}
          className={imageStylesClass}
          loading={loading}
          onLoad={onLoad}
          onError={onError}
          ref={handleImgRef}
          animate={imageControls}
          initial={{ scale: isImageAlreadyLoaded ? 1 : 1.2 }}
          {...rest}
        />
      )}

      {state.status === 'error' && (
        <ErrorState alt={alt} allowRetry={allowRetry} retryCount={state.retryCount} handleRetry={handleRetry} />
      )}
    </div>
  );
};

const imageContainerStyle = css`
  position: relative;
  overflow: hidden;
  max-width: 100%;
`;

const imageStyle = css`
  width: 100%;
  height: 100%;
`;

const placeholderStyle = css`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: ${theme.zIndex.low};
`;

const placeholderImgExtraStyle = css`
  background: black;
`;

const errorContainerStyle = css`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${theme.colors.neutral5};
  border: 1px solid ${theme.colors.neutral20};
  border-radius: inherit;
  z-index: ${theme.zIndex.middle};
`;

const errorIconStyle = css`
  margin-bottom: ${theme.spacing(0.5)};
`;

const errorTextContainerStyle = css`
  margin-top: ${theme.spacing(2)};
`;

const errorTextMarginZero = css`
  margin: 0;
`;

const errorTextSubStyle = css`
  padding: ${theme.spacing(0.5)};
  border: 1px solid ${theme.colors.neutral20};
  border-radius: ${theme.borderRadius.medium};
  background: ${theme.colors.white};
`;

const objectFitStyles = {
  fill: css`
    object-fit: fill;
  `,
  cover: css`
    object-fit: cover;
  `,
  contain: css`
    object-fit: contain;
  `,
};

export { ImageComponent as Image };
